/**
 * Findings.tsx — V4-4-7 / ADR-0029.
 *
 * Workspace-scoped findings page: release_gap, oversight_gap, and
 * component-delta findings with SLA clock, owner, source (system /
 * entry field / check).
 *
 * Filters (deep links): ?control=<id> (SOC 2 "Fix failing check"); V113-7-2:
 * ?system=<id> and ?source=monitoring (Monitoring "Open findings"), filtered by
 * the API inside the session's workspace (another workspace's id lists nothing); PR A F1:
 * ?requirement=<ref>[,<ref>…]&program=<key>&period=<id> on its own (CMMC
 * scorecard rows) — requirement → objectives → tests → controls, from the
 * program's tests (any member, session-scoped) — with a "Filtered to …" chip,
 * Clear, and a way back (`return`, same-app paths only).
 *
 * Auth: workspace member read; owner/admin/assignee transition.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  PageHeader,
  SeverityBadge,
  Spinner,
} from '@aigovops/design-system';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { getRemediationSummary, getSoc2Readiness, listAllControls, listProgramTests, req } from '../lib/api';
import { reqRef } from '../lib/cmmc-model';
import { findingKindLabel, findingStatusLabel, fmtDate } from '../lib/format';
import { useQuery } from '@tanstack/react-query';
import { useWorkspace } from '../lib/workspace';
import { RemediationDraft } from '../components/RemediationDraft';
import { RequirementFix } from '../components/programs/CmmcAssess';
import { usePrograms } from '../lib/workspace-navigation';
import { getMonitoringSummary } from '../lib/monitoring-summary';
import './findings-filter.css';

interface FindingRow {
  finding_id: string;
  system_id: string | null;
  kind: string;
  status: string;
  /** What the API returns (V101-6-3: the page read `title`, which only the fixtures had). */
  detail: string | null;
  severity: string;
  owner_user_id: string | null;
  sla_deadline_at: string | null;
  sla_breached: number;
  source_kind: string | null;
  opened_at: string;
  /** Control the finding was raised against (control-check findings); null otherwise. */
  control_id?: string | null;
  certification_program?: string | null;
}

type KindFilter = 'all' | 'release_gap' | 'oversight_gap' | 'component_delta';

const KIND_LABELS: Record<string, string> = {
  release_gap: 'Release gap',
  oversight_gap: 'Oversight gap',
  component_delta: 'Component delta',
};

/** V113-7-2: the server filters ?system= / ?source= (workspace-scoped); `filters` is already the query string. */
async function listFindings(slug: string, filters: string): Promise<{ findings: FindingRow[] }> {
  return req<{ findings: FindingRow[] }>(`/api/w/${encodeURIComponent(slug)}/findings${filters ? `?${filters}` : ''}`);
}

const SERVER_FILTERS = ['system', 'source'] as const;

function mapSeverity(s: string): 'critical' | 'high' | 'elevated' | 'moderate' | 'standard' {
  const map: Record<string, 'critical' | 'high' | 'elevated' | 'moderate' | 'standard'> = {
    critical: 'critical', high: 'high', medium: 'moderate', low: 'standard',
  };
  return map[s] ?? 'standard';
}

export function Findings() {
  // V96-4-9: SOC 2 "Fix failing check" deep-links with ?control=<id>.
  const [searchParams, setSearchParams] = useSearchParams();
  const systemFilter = searchParams.get('system');
  const sourceFilter = searchParams.get('source') === 'monitoring' ? 'monitoring' : null;
  const serverFilters = new URLSearchParams([
    ...(systemFilter ? [['system', systemFilter]] : []),
    ...(sourceFilter ? [['source', sourceFilter]] : []),
  ] as string[][]).toString();
  const q = useWorkspaceQuery(
    (s) => [...queryKeys.findings(s), serverFilters],
    (s) => listFindings(s, serverFilters),
  );
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const controlFilter = searchParams.get('control');
  const requirementRef = searchParams.get('requirement');
  const clearControl = () => {
    const next = new URLSearchParams(searchParams);
    for (const k of ['control', 'requirement', 'program', 'period', ...SERVER_FILTERS]) next.delete(k);
    setSearchParams(next, { replace: true });
  };
  // Chip label: the SOC 2 criterion ("CC6.1 Logical access") when we came from
  // SOC 2, else the control title — both from cached, member-readable queries.
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug;
  const soc2 = useQuery({ queryKey: queryKeys.soc2Readiness(slug, false), queryFn: () => getSoc2Readiness(slug, false), enabled: !!controlFilter && !!requirementRef, retry: false });
  // V101-5-4: every row's coach state in one request (the Fix summary badge; closed findings keep their plan).
  const coach = useQuery({ queryKey: queryKeys.remediationSummary(slug), queryFn: () => getRemediationSummary(slug), enabled: !!slug, retry: false, staleTime: 30_000 });
  const controls = useQuery({ queryKey: queryKeys.allControls(slug), queryFn: () => listAllControls(slug), enabled: !!controlFilter && !requirementRef, retry: false, staleTime: 5 * 60_000 });
  const requirement = requirementRef ? soc2.data?.requirements.find((r) => r.ref === requirementRef) : undefined;
  // F1: a requirement filter on its own (CMMC) maps requirement → objectives → tests → controls.
  const programKey = searchParams.get('program');
  const periodId = searchParams.get('period');
  const reqRefs = useMemo(() => (!controlFilter && requirementRef ? requirementRef.split(',').map((r) => r.trim()).filter(Boolean) : []), [controlFilter, requirementRef]);
  const tests = useQuery({
    queryKey: queryKeys.programTests(slug, programKey ?? '', periodId ?? ''),
    queryFn: () => listProgramTests(slug, programKey!, periodId!),
    enabled: reqRefs.length > 0 && !!programKey && !!periodId, retry: false, staleTime: 5 * 60_000,
  });
  const reqControls = useMemo(() => new Set((tests.data?.tests ?? [])
    .filter((t) => t.control_id && t.objectives.some((o) => reqRefs.includes(reqRef(o.objective_id))))
    .map((t) => t.control_id!)), [tests.data, reqRefs]);
  const reqFilter = reqRefs.length > 0;
  // F1: the program card the requirement's next step needs (same read as /programs).
  const programs = usePrograms(reqFilter && programKey ? slug : undefined);
  const programCard = programs.data?.programs.find((p) => p.program === programKey || p.profile_id === programKey) ?? null;
  const ret = searchParams.get('return');
  const backTo = ret && ret.startsWith(`/w/${slug}/app/`) ? ret : null;
  const matches = (f: FindingRow) => (!controlFilter || f.control_id === controlFilter) && (!reqFilter || (!!f.control_id && reqControls.has(f.control_id)));
  const controlTitle = controls.data?.controls.find((c) => c.control_id === controlFilter)?.title;
  // V113-7-2: the system's name for the chip (member read, cached with the Monitoring page).
  const monSummary = useQuery({ queryKey: ['monitoring', slug, 'summary'], queryFn: () => getMonitoringSummary(slug), enabled: !!systemFilter && !!slug, retry: false, staleTime: 60_000 });
  const systemName = systemFilter ? monSummary.data?.systems.find((x) => x.system_id === systemFilter)?.name ?? null : null;
  const scopeLabel = [systemFilter ? (systemName ?? 'This system') : null, sourceFilter ? 'monitoring' : null].filter(Boolean).join(' · ');
  const filterLabel = requirement ? `${requirement.ref} ${requirement.title}` : requirementRef ?? controlTitle ?? `Control ${controlFilter}`;

  const filtered = useMemo(() => {
    const all = (q.data?.findings ?? []).filter(matches);
    if (kindFilter === 'all') return all;
    return all.filter((f) => f.kind === kindFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, kindFilter, controlFilter, reqFilter, reqControls]);

  // V98-3 review U1: Today / the item page deep-link to one finding (?finding=<id>) — scroll to it and focus it.
  const focusFinding = searchParams.get('finding');
  const loaded = !!q.data;
  useEffect(() => {
    if (!focusFinding || !loaded) return;
    const el = document.getElementById(`finding-${focusFinding}`);
    el?.scrollIntoView?.({ block: 'center' });
    el?.focus();
  }, [focusFinding, loaded]);

  const counts = useMemo(() => {
    // Header and tab counts follow the control filter.
    const all = (q.data?.findings ?? []).filter(matches);
    return {
      all: all.length,
      release_gap: all.filter((f) => f.kind === 'release_gap').length,
      oversight_gap: all.filter((f) => f.kind === 'oversight_gap').length,
      component_delta: all.filter((f) => f.kind === 'component_delta').length,
      breached: all.filter((f) => f.sla_breached).length,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, controlFilter, reqFilter, reqControls]);

  if (q.loading || (reqFilter && tests.isLoading)) return <Spinner label="Loading findings" />;
  if (q.error) return <Alert variant="error">{q.error}</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <PageHeader
        title="Findings"
        subtitle={`${counts.all} finding${counts.all !== 1 ? 's' : ''}${counts.breached ? ` · ${counts.breached} SLA breached` : ''}`}
      />

      {/* F20: a requirement filter shows that requirement's findings only — no kind tabs. */}
      {reqFilter ? null : <div
        role="tablist"
        aria-label="Kind filter"
        style={{ display: 'flex', gap: 'var(--space-1)', fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)' }}
      >
        {(['all', 'release_gap', 'oversight_gap', 'component_delta'] as const).map((k) => (
          <button
            key={k}
            role="tab"
            aria-selected={kindFilter === k}
            onClick={() => setKindFilter(k)}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              border: 'none',
              borderBottom: kindFilter === k ? '2px solid var(--accent)' : '2px solid transparent',
              background: 'transparent',
              color: kindFilter === k ? 'var(--text)' : 'var(--text-muted)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-2xs)',
              fontWeight: kindFilter === k ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {k === 'all' ? `All (${counts.all})` : `${KIND_LABELS[k] ?? k} (${counts[k]})`}
          </button>
        ))}
      </div>}

      {reqFilter ? (
        <div className="v96-filter-chip" role="status" data-part="requirement-filter">
          <span>{`Filtered to ${reqRefs.join(', ')}`}</span>
          <span aria-hidden="true">·</span>
          <button type="button" onClick={clearControl}>Clear</button>
          {backTo ? <><span aria-hidden="true">·</span><Link to={backTo}>Back to the program</Link></> : null}
        </div>
      ) : null}
      {scopeLabel && !controlFilter ? (
        <div className="v96-filter-chip" role="status" data-part="scope-filter">
          <span>{`Filtered to ${scopeLabel}`}</span>
          <span aria-hidden="true">·</span>
          <button type="button" onClick={clearControl}>Clear filter</button>
        </div>
      ) : null}
      {controlFilter ? (
        <div className="v96-filter-chip" role="status">
          <span>{filterLabel}</span>
          <span aria-hidden="true">·</span>
          <button type="button" onClick={clearControl}>Clear filter</button>
        </div>
      ) : null}

      {filtered.length === 0 && reqFilter ? (
        // F1: no finding is open for these requirements (no "create" route exists — findings are opened by checks).
        <EmptyState
          title={`No open findings for ${reqRefs.join(', ')}`}
          action={<span className="v96-filter-actions">
            {/* F1: without a finding, the requirement's own next step by gap type — never a link back to where the user came from. */}
            {programCard && periodId
              ? <RequirementFix slug={slug} prefix={`/w/${slug}/app`} card={programCard} periodId={periodId} refs={reqRefs} role={role} viewerId="" />
              : null}
            <button type="button" onClick={clearControl}>Show all findings</button>
          </span>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={controlFilter ? `No findings for ${filterLabel}` : scopeLabel ? `No findings for ${scopeLabel}` : 'No findings'}
          description={controlFilter ? 'The failing check has not opened a finding yet.' : kindFilter === 'all' ? 'No findings recorded.' : `No ${KIND_LABELS[kindFilter] ?? kindFilter} findings.`}
          action={scopeLabel && !controlFilter ? <span className="v96-filter-actions"><button type="button" onClick={clearControl}>Show all findings</button></span> : controlFilter ? <span className="v96-filter-actions"><button type="button" onClick={clearControl}>Show all findings</button><Link to="../setup#security-posture" relative="path">Check connections</Link></span> : undefined}
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {filtered.map((f) => (
            <div key={f.finding_id} id={`finding-${f.finding_id}`} tabIndex={-1} className="gp-finding" data-focused={f.finding_id === focusFinding || undefined}><Card>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-4)',
              }}>
                <SeverityBadge severity={mapSeverity(f.severity)} />
                <span style={{
                  flex: '1 1 200px',
                  minWidth: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-2xs)',
                  fontWeight: 500,
                  color: 'var(--text)',
                }}>
                  {/* F1: each finding opens its item page. */}
                  <Link to={`/w/${slug}/app/items/finding/${encodeURIComponent(f.finding_id)}${programKey ? `?from=program:${encodeURIComponent(programKey)}` : ''}`} data-part="finding-link">
                    {f.detail || findingKindLabel(f.kind)}
                  </Link>
                </span>
                <Badge tone="neutral">{findingKindLabel(f.kind)}</Badge>
                <span style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-3xs)',
                  color: f.sla_breached ? 'var(--danger-fg)' : 'var(--text-dim)',
                  fontWeight: f.sla_breached ? 600 : 400,
                }}>
                  {findingStatusLabel(f.status)}
                  {f.sla_breached ? ' · SLA breached' : ''}
                </span>
                {f.sla_deadline_at && (
                  <span style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-3xs)',
                    color: 'var(--text-dim)',
                  }}>
                    {`Due ${fmtDate(f.sla_deadline_at)}`}
                  </span>
                )}
              </div>
              {/* V101-5-4: the remediation coach — open control-check findings only (renders nothing otherwise). */}
              {f.kind === 'control_check_failure' && !coach.isLoading && ((f.status === 'open' || f.status === 'acknowledged') || coach.data?.summary[f.finding_id]?.plan_accepted) ? (
                <div style={{ padding: '0 var(--space-4) var(--space-3)' }}>
                  <RemediationDraft slug={slug} prefix={`/w/${slug}/app`} role={role} finding={{ ...f, title: f.detail }} summary={coach.isSuccess ? coach.data.summary[f.finding_id] ?? null : undefined} />
                </div>
              ) : null}
            </Card></div>
          ))}
        </div>
      )}
    </div>
  );
}
